import React, { useMemo, useState } from 'react';
import { ClipboardList, RefreshCw, Search } from 'lucide-react';
import { NumberedPagination, PageHeader } from '@kioskgaming/ui';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { LoadingSpinner } from '../../ui/Loading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/Table';
import { useAdminAuditLogs } from '../../../hooks/useApi';

export type AdminAuditLogRow = {
  id: string;
  adminId?: string | null;
  action: string;
  targetType: string;
  targetId: string;
  details?: {
    before?: unknown;
    after?: unknown;
    [key: string]: unknown;
  } | null;
  notes?: string | null;
  ipAddress?: string | null;
  createdAt: string;
  actor?: {
    id: string;
    username?: string | null;
    name?: string | null;
    role?: string | null;
  } | null;
};

const ACTION_OPTIONS = [
  '',
  'create_admin',
  'update_admin',
  'terminate_admin',
  'delete_admin',
  'update_admin_permissions',
  'terminate_player',
  'terminate_agent',
  'approve_withdrawal',
  'reject_withdrawal',
];

const TARGET_OPTIONS = ['', 'admin', 'user', 'agent', 'withdrawal', 'shop', 'super_agent'];

function formatJson(value: unknown): string {
  if (value == null) return '—';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

const AuditLogsPage: React.FC = () => {
  const [draft, setDraft] = useState({
    actorId: '',
    targetType: '',
    targetId: '',
    action: '',
    dateFrom: '',
    dateTo: '',
  });
  const [filters, setFilters] = useState({ ...draft, page: 1, limit: 20 });
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = {
      page: filters.page,
      limit: filters.limit,
    };
    if (filters.actorId.trim()) params.actorId = filters.actorId.trim();
    if (filters.targetType) params.targetType = filters.targetType;
    if (filters.targetId.trim()) params.targetId = filters.targetId.trim();
    if (filters.action) params.action = filters.action;
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    return params;
  }, [filters]);

  const { data, isLoading, isFetching, refetch, error } = useAdminAuditLogs(queryParams);
  const logs: AdminAuditLogRow[] = data?.data?.logs ?? [];
  const pagination = data?.data?.pagination ?? { page: 1, limit: 20, total: 0, pages: 1 };

  const applyFilters = () => setFilters({ ...draft, page: 1, limit: filters.limit });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Audit log"
        description="Privileged admin actions with before/after snapshots. Visible to System and Super Admin only."
        titleAddon={<ClipboardList className="h-7 w-7 text-slate-700" />}
      />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-base">Filters</CardTitle>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
          <Input
            placeholder="Actor admin ID"
            value={draft.actorId}
            onChange={(e) => setDraft((d) => ({ ...d, actorId: e.target.value }))}
          />
          <select
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            value={draft.targetType}
            onChange={(e) => setDraft((d) => ({ ...d, targetType: e.target.value }))}
          >
            {TARGET_OPTIONS.map((t) => (
              <option key={t || 'any'} value={t}>
                {t ? `Target: ${t}` : 'Any target type'}
              </option>
            ))}
          </select>
          <Input
            placeholder="Target ID"
            value={draft.targetId}
            onChange={(e) => setDraft((d) => ({ ...d, targetId: e.target.value }))}
          />
          <select
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            value={draft.action}
            onChange={(e) => setDraft((d) => ({ ...d, action: e.target.value }))}
          >
            {ACTION_OPTIONS.map((a) => (
              <option key={a || 'any'} value={a}>
                {a ? a : 'Any action'}
              </option>
            ))}
          </select>
          <Input
            type="date"
            value={draft.dateFrom}
            onChange={(e) => setDraft((d) => ({ ...d, dateFrom: e.target.value }))}
          />
          <Input
            type="date"
            value={draft.dateTo}
            onChange={(e) => setDraft((d) => ({ ...d, dateTo: e.target.value }))}
          />
          <div className="md:col-span-3 xl:col-span-6">
            <Button onClick={applyFilters}>
              <Search className="mr-2 h-4 w-4" />
              Apply filters
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <p className="text-sm text-rose-600">Failed to load audit logs.</p>
          ) : logs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No audit rows for these filters.</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Actor</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Target</TableHead>
                    <TableHead>IP</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => {
                    const open = expandedId === log.id;
                    const actorLabel =
                      log.actor?.username || log.actor?.name || log.adminId || 'system';
                    return (
                      <React.Fragment key={log.id}>
                        <TableRow>
                          <TableCell className="whitespace-nowrap text-xs">
                            {log.createdAt ? new Date(log.createdAt).toLocaleString() : '—'}
                          </TableCell>
                          <TableCell className="text-sm">{actorLabel}</TableCell>
                          <TableCell className="font-mono text-xs">{log.action}</TableCell>
                          <TableCell className="font-mono text-xs">
                            {log.targetType}:{log.targetId}
                          </TableCell>
                          <TableCell className="text-xs">{log.ipAddress || '—'}</TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setExpandedId(open ? null : log.id)}
                            >
                              {open ? 'Hide' : 'Before/After'}
                            </Button>
                          </TableCell>
                        </TableRow>
                        {open && (
                          <TableRow>
                            <TableCell colSpan={6}>
                              <div className="grid gap-3 md:grid-cols-2">
                                <div>
                                  <p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
                                    Before
                                  </p>
                                  <pre className="max-h-64 overflow-auto rounded-md bg-slate-50 p-3 text-xs">
                                    {formatJson(log.details?.before)}
                                  </pre>
                                </div>
                                <div>
                                  <p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
                                    After
                                  </p>
                                  <pre className="max-h-64 overflow-auto rounded-md bg-slate-50 p-3 text-xs">
                                    {formatJson(log.details?.after)}
                                  </pre>
                                </div>
                              </div>
                              {log.notes ? (
                                <p className="mt-2 text-xs text-muted-foreground">Notes: {log.notes}</p>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
              <div className="mt-4 flex justify-end">
                <NumberedPagination
                  currentPage={pagination.page}
                  totalPages={Math.max(1, pagination.pages || 1)}
                  onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogsPage;
